import * as React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  Eye,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  TriangleAlert,
  Video,
} from 'lucide-react'
import { MuxVideo } from '../-components/MuxVideo'
import { MediaLibraryPicker } from '../-components/MediaLibraryPicker'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  useDeleteLearningLibraryAsset,
  useDetachLearningLibraryAsset,
  useLearningLibrary,
  useLearningWatchStats,
  useRenameLearningLibraryAsset,
  useReplaceLearningLibraryAsset,
} from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'
import { formatDate } from '@/lib/formatters'

export const Route = createFileRoute('/_app/learning/admin/media')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
  component: RouteComponent,
})

function duration(seconds: number | null): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Hours where that reads sensibly, minutes where it does not. */
function formatHours(ms: number): string {
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${minutes} min`
  const hours = minutes / 60
  return `${hours.toFixed(hours < 10 ? 1 : 0)} hrs`
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function size(bytes: number | null): string {
  if (!bytes) return ''
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * The media library (sc-1335).
 *
 * Videos were invisible outside the lesson that used them: one uploaded into a
 * lesson that was never saved stayed on Mux, billing, unreachable. This is
 * where they can be found, corrected and cleaned up.
 *
 * Unused assets are called out rather than hidden. They are the ones costing
 * money for nothing, and the ones somebody is most likely hunting for.
 */
function RouteComponent() {
  const [query, setQuery] = React.useState('')
  const [tag, setTag] = React.useState<string | null>(null)

  const { data: assets = [], isPending } = useLearningLibrary({
    q: query.trim() || undefined,
    tag: tag ?? undefined,
  })
  const rename = useRenameLearningLibraryAsset()
  const remove = useDeleteLearningLibraryAsset()
  const detach = useDetachLearningLibraryAsset()
  const replace = useReplaceLearningLibraryAsset()
  const { data: stats } = useLearningWatchStats()

  // Which row is expanded to show its player, and which is being swapped.
  const [playing, setPlaying] = React.useState<string | null>(null)
  const [replacing, setReplacing] = React.useState<string | null>(null)

  const [editing, setEditing] = React.useState<string | null>(null)
  const [draftTitle, setDraftTitle] = React.useState('')

  // Every tag in view, so filtering is a click rather than a guess at spelling.
  const tags = React.useMemo(() => {
    const seen = new Map<string, number>()
    for (const asset of assets) {
      for (const value of asset.tags) {
        seen.set(value, (seen.get(value) ?? 0) + 1)
      }
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20)
  }, [assets])

  const unused = assets.filter((asset) => asset.usedBy.length === 0).length

  return (
    <div className="py-4">
      <PageTitle title="Learning — Media library" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Courses
        </Link>
      </Button>

      <div className="mb-4">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          Media library
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every video uploaded to Learning. Titles are generated from the audio
          — correct one here if it is wrong. Play counts come from Mux and
          measure engagement, not completion: finishing a lesson is recorded
          separately.
          {unused > 0 && (
            <>
              {' '}
              <span className="text-amber-700 dark:text-amber-400">
                {unused} {unused === 1 ? 'video is' : 'videos are'} not used
                anywhere and still cost storage.
              </span>
            </>
          )}
        </p>
      </div>

      {/* ── Rollups ─────────────────────────────────────────────────── */}
      {stats && stats.totals.views > 0 && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <StatTile label="Views" value={stats.totals.views.toLocaleString()} />
          <StatTile
            label="Watched"
            value={formatHours(stats.totals.watchTimeMs)}
          />
          <StatTile
            label="Videos played"
            value={`${stats.totals.videosWatched} of ${stats.totals.videosTotal}`}
          />
        </div>
      )}

      {/* ── Search ──────────────────────────────────────────────────── */}
      <div className="mb-3 space-y-2">
        <div className="relative">
          <Search
            className="absolute top-2.5 left-2.5 size-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by title, file name or tag…"
            className="pl-8"
          />
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tag && (
              <Button
                size="sm"
                variant="secondary"
                className="h-7"
                onClick={() => setTag(null)}
              >
                Clear filter
              </Button>
            )}
            {tags.map(([value, count]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTag(tag === value ? null : value)}
                className={
                  tag === value
                    ? 'rounded-full bg-primary px-2.5 py-1 text-xs text-primary-foreground'
                    : 'rounded-full border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/60'
                }
              >
                {value}
                <span className="ml-1 tabular-nums opacity-60">{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── List ────────────────────────────────────────────────────── */}
      {isPending ? (
        <div className="flex justify-center py-16">
          <Loader2
            className="size-5 animate-spin text-muted-foreground"
            aria-hidden
          />
        </div>
      ) : assets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            {query || tag ? 'Nothing matches that.' : 'No videos uploaded yet.'}
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {assets.map((asset) => (
            <li key={asset.id}>
              <Card>
                <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
                  <Video
                    className="mt-0.5 hidden size-5 shrink-0 text-muted-foreground sm:block"
                    aria-hidden
                  />

                  <div className="min-w-0 flex-1 space-y-1.5">
                    {editing === asset.id ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          autoFocus
                          value={draftTitle}
                          onChange={(event) =>
                            setDraftTitle(event.target.value)
                          }
                          className="h-8 max-w-md"
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') setEditing(null)
                            if (event.key === 'Enter') {
                              rename.mutate(
                                { assetId: asset.id, title: draftTitle },
                                { onSuccess: () => setEditing(null) },
                              )
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={rename.isPending}
                          onClick={() =>
                            rename.mutate(
                              { assetId: asset.id, title: draftTitle },
                              { onSuccess: () => setEditing(null) },
                            )
                          }
                        >
                          Save
                        </Button>
                        {/* Clearing returns to the generated title, so a bad
                            correction is not something you are stuck with. */}
                        {asset.title && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              rename.mutate(
                                { assetId: asset.id, title: '' },
                                { onSuccess: () => setEditing(null) },
                              )
                            }
                          >
                            Use the generated title
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="text-left text-sm font-medium hover:underline"
                        onClick={() => {
                          setDraftTitle(asset.displayName)
                          setEditing(asset.id)
                        }}
                        title="Correct this title"
                      >
                        {asset.displayName}
                        {!asset.title && asset.summaryStatus === 'READY' && (
                          <Sparkles
                            className="ml-1.5 inline size-3 align-[-1px] text-muted-foreground"
                            aria-label="Generated title"
                          />
                        )}
                      </button>
                    )}

                    {asset.description && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {asset.description}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="truncate">{asset.originalFileName}</span>
                      {asset.durationSeconds != null && (
                        <span className="tabular-nums">
                          {duration(asset.durationSeconds)}
                        </span>
                      )}
                      {asset.byteSize != null && (
                        <span>{size(asset.byteSize)}</span>
                      )}
                      <span>{formatDate(asset.createdAt)}</span>
                      {asset.transcriptStatus &&
                        asset.transcriptStatus !== 'READY' && (
                          <span>
                            transcript {asset.transcriptStatus.toLowerCase()}
                          </span>
                        )}
                      {/* Engagement, not completion — a view is somebody
                          pressing play. Finishing a lesson is recorded in our
                          own tables and the two will not agree. */}
                      {stats?.perAsset[asset.id] && (
                        <span className="inline-flex items-center gap-1 text-foreground">
                          <Eye className="size-3" aria-hidden />
                          {stats.perAsset[asset.id].views.toLocaleString()}{' '}
                          {stats.perAsset[asset.id].views === 1
                            ? 'play'
                            : 'plays'}
                          {stats.perAsset[asset.id].watchTimeMs > 0 && (
                            <>
                              {' '}
                              ·{' '}
                              {formatHours(
                                stats.perAsset[asset.id].watchTimeMs,
                              )}{' '}
                              watched
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    {asset.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {asset.tags.map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setTag(value)}
                            className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted/70"
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* What would break if this were deleted. */}
                    {asset.usedBy.length > 0 ? (
                      <ul className="space-y-1">
                        {asset.usedBy.map((use, index) => (
                          <li
                            key={`${use.label}-${index}`}
                            className="flex flex-wrap items-center gap-2 text-xs"
                          >
                            {/* Linked, not just named. Knowing a video is used
                                somewhere is no help without a way to get
                                there. */}
                            {use.courseVersionId ? (
                              <Link
                                to="/learning/admin/$versionId"
                                params={{ versionId: use.courseVersionId }}
                                className="text-primary hover:underline"
                              >
                                {use.label}
                              </Link>
                            ) : (
                              <span>{use.label}</span>
                            )}
                            <span className="text-muted-foreground">
                              {use.kind}
                            </span>
                            {use.lessonId && (
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-destructive hover:underline"
                                disabled={detach.isPending}
                                onClick={() =>
                                  detach.mutate({
                                    assetId: asset.id,
                                    lessonId: use.lessonId as string,
                                  })
                                }
                              >
                                remove from this lesson
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                        <TriangleAlert className="size-3.5" aria-hidden />
                        Not used anywhere
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setPlaying(playing === asset.id ? null : asset.id)
                      }
                    >
                      <Play className="mr-1.5 size-4" aria-hidden />
                      {playing === asset.id ? 'Hide' : 'Watch'}
                    </Button>

                    {/* The way out of "in use, so you can do nothing": swap
                        every reference to a better upload, after which the old
                        one is unused and deletable. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReplacing(asset.id)}
                    >
                      <RefreshCw className="mr-1.5 size-4" aria-hidden />
                      Replace
                    </Button>

                    {asset.usedBy.length === 0 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ assetId: asset.id })}
                      >
                        <Trash2
                          className="mr-1.5 size-4 text-destructive"
                          aria-hidden
                        />
                        Delete
                      </Button>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        {asset.usedBy.length} in use
                      </Badge>
                    )}
                  </div>
                </CardContent>

                {/* Watching it is how you tell two similar recordings apart —
                    a filename and a duration are not enough. */}
                {playing === asset.id && (
                  <CardContent className="pt-0 pb-4">
                    <MuxVideo assetId={asset.id} title={asset.displayName} />
                  </CardContent>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      {/* Pick the replacement from the same library. Everything pointing at
          the old video is repointed, which is deliberate — and why the usage
          list above it is visible before you get here. */}
      {replacing && (
        <MediaLibraryPicker
          open
          onOpenChange={(next) => {
            if (!next) setReplacing(null)
          }}
          onPick={(withAssetId) => {
            replace.mutate(
              { assetId: replacing, withAssetId },
              { onSuccess: () => setReplacing(null) },
            )
          }}
        />
      )}
    </div>
  )
}
